import React from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { ChevronRight, RefreshCw, XCircle, Coins, CreditCard, Lock } from "lucide-react";

export default function Help() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
      <Header title="Quick help" onBack={() => nav(-1)} />
      <main className="max-w-md mx-auto px-5 py-5 space-y-4" data-testid="help-page">
        <div>
          <h2 className="text-2xl text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>
            Closing the till — quick rules
          </h2>
          <p className="text-[#2B533E] mt-2">Plain-language reminders. Nobody's in trouble for an honest note.</p>
        </div>

        <Card title="Refund vs. Void" icon={<RefreshCw size={18} />}>
          <p><strong>Void = cancel before the sale finishes.</strong> No money moves. Use it if you rang something wrong before the customer paid.</p>
          <p><strong>Refund = give money back after the sale.</strong> Cash leaves the drawer (or the card gets credited). A refund moves real money — that's why a refund-instead-of-void leaves the drawer short.</p>
          <p className="text-[#5C7566] text-sm">When in doubt: void first, refund only when the sale is already closed.</p>
        </Card>

        <Card title="Cash vs. Card" icon={<CreditCard size={18} />}>
          <p>Always tell the register what payment method the customer used. If you ring a card sale as cash (or cash as card), the totals won't match and the till will look off.</p>
          <p className="text-[#5C7566] text-sm">If you catch this mid-shift, write a note for the close — don't try to "fix it" in the drawer.</p>
        </Card>

        <Card title="The card line covers ALL cards" icon={<CreditCard size={18} />}>
          <p>
            On the Grill register, the card total prints as <strong>VISA SALES</strong> or <strong>VISA-IN-D</strong>.
            The label says "VISA" but the number already includes <strong>Visa + MasterCard + Debit</strong> combined.
          </p>
          <p className="text-[#5C7566] text-sm">
            Sanity check: <code>CASH-IN-D + VISA-IN-D = DRWR TTL</code>. If they add up to the drawer total, the card line is right —
            no need to add MC manually.
          </p>
        </Card>

        <Card title="Tips" icon={<Coins size={18} />}>
          <p><strong>Card tips:</strong> paid in cash from the locked <strong>tip float box ($375)</strong>. Never from the till.</p>
          <p><strong>Cash tips:</strong> go in the jar. Never the drawer.</p>
          <p className="text-[#5C7566] text-sm">When you finish a close, the app tells you exactly how much cash to take out of the tip float box and give to whoever worked.</p>
          <p className="text-[#7F1D1D] text-sm bg-[#B91C1C]/10 border border-[#B91C1C]/20 rounded-lg p-2 mt-2">
            ⚠ The old paper Daily Balance Sheet says "TIPS TO BE TAKEN OUT OF CASH" — <strong>ignore that line.</strong> Taking tips from the till is what causes most random shorts and overs.
          </p>
        </Card>

        <Card title="Till float" icon={<Lock size={18} />}>
          <p>Each till starts every shift with its float in change:</p>
          <ul className="list-disc pl-5 space-y-1 text-[#2B533E]">
            <li><strong>Pro Shop:</strong> $300</li>
            <li><strong>Grill (Makwa):</strong> $150</li>
          </ul>
          <p>After the close, count that float back in. The rest goes in the deposit envelope.</p>
          <p className="text-[#5C7566] text-sm">If the float is short, don't pull from the deposit. Note it on the close.</p>
        </Card>

        <Card title="When the till is short" icon={<XCircle size={18} />}>
          <p>Run through this in your head before you sign off:</p>
          <ul className="list-disc pl-5 space-y-1 text-[#2B533E]">
            <li>Did anyone do a <strong>refund</strong> today?</li>
            <li>Was a <strong>void</strong> run after the receipt printed?</li>
            <li>Could a card sale have been rung as <strong>cash</strong>?</li>
            <li>Did anyone <strong>borrow from the till float</strong>?</li>
            <li>Was cash taken out for tips? (It shouldn't have been.)</li>
            <li>Any chance wrong change was handed back?</li>
          </ul>
          <p className="text-[#5C7566] text-sm">Write what you find in the Note. The owner will check.</p>
        </Card>

        <button
          data-testid="help-back"
          onClick={() => nav(-1)}
          className="w-full h-12 rounded-2xl bg-[#1A5D3A] text-white text-sm flex items-center justify-center gap-2 active:scale-[0.99]"
        >
          Back to the close <ChevronRight size={16} />
        </button>
      </main>
    </div>
  );
}

function Card({ title, icon, children }) {
  return (
    <section className="bg-white rounded-2xl border border-[#E8E4DA] p-5 space-y-2">
      <div className="flex items-center gap-2 text-[#1A5D3A]">
        {icon}
        <h3 className="text-lg text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>{title}</h3>
      </div>
      <div className="space-y-2 text-[#2B533E]">{children}</div>
    </section>
  );
}
